import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { Drawer, Tab, Tooltip } from "@mui/material";
import StyledButton from "components/shared/StyledButton";
import { getCurrencyData } from "helpers/utils";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { fetchBeneficiar } from "store/beneficiaryStore";
import type { GeneralTabTerm } from "store/managementStore";
import { ITerm } from "store/managementStore";
import { fetchPeriodExchangeRates } from "store/periodStore";
import type { ICompany } from "store/settingsStore";
import { resetStatements } from "store/statementsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { PeriodStatus } from "store/types";

import variables from "../../../assets/styles/_variables.module.scss";
import ButtonBase from "../ButtonBase";
import { StyledTabs } from "../StyledTabs";
import TitleCover from "../TitleCover";
import TitleStatement from "../TitleStatement";
import SalesDetails from "./SalesDetails";
import styles from "./style.module.scss";

export type TAnchor = "left" | "right" | "top" | "bottom";
export type TToggleDrawer = (anchor: TAnchor, open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => void;

type TProps = {
    toggleDrawer: TToggleDrawer;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isOpen: boolean;
    currentTerm: GeneralTabTerm;
    tabId: number | null;
    activePeriod: number | undefined;
    usesMultipleCurrencies: boolean;
    companyInfos: ICompany | undefined;
};
type TTabPanelProps = {
    children?: React.ReactNode;
    index: number;
    value: number;
};

export function TabPanel(props: TTabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: (index != 0 && "25px") || 0,
                marginTop: "20px",
                padding: (index != 0 && "0 48px") || "unset",
            }}
            role={"tabpanel"}
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && <>{children}</>}
        </div>
    );
}
/**
 * Drawer with statements and sales for a title (on Management > General)
 */
function ManagementTitleDetails({
    toggleDrawer,
    setOpen,
    isOpen,
    currentTerm,
    tabId,
    activePeriod,
    usesMultipleCurrencies,
    companyInfos,
}: TProps) {
    const [t] = useTranslation();
    const [currentTab, setCurrentTab] = useState<number | null>(tabId);
    const dispatch = useAppDispatch();
    const { periodExchangeRates } = useAppSelector((state) => state.period);
    const periodStatus = useAppSelector((state) => state.period.activePeriodData?.isActive) as PeriodStatus | undefined;
    const { beneficiar: beneficiary } = useAppSelector((state) => state.beneficiary);
    const { statement } = useAppSelector((state) => state.statements);

    const hasCollectiveManagement =
        (currentTerm.hasPostCloseCollectiveManagementSales ?? 0) > 0 && periodStatus === PeriodStatus.CLOSED;
    const drawerPaperRef = useRef<HTMLDivElement | null>(null);
    const [drawerLeft, setDrawerLeft] = useState<number>(0);
    const [showCMInfoModal, setShowCMInfoModal] = useState(false);

    const [isStatementCurrencyDefault, setIsStatementCurrencyDefault] = useState(true);
    const [showEchangeRate, setShowExchangeRate] = useState(false);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    useEffect(() => {
        if (!isOpen) {
            dispatch(resetStatements());
            setShowExchangeRate(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && hasCollectiveManagement) {
            setShowCMInfoModal(false);
            const timer = setTimeout(() => {
                if (drawerPaperRef.current) {
                    setDrawerLeft(drawerPaperRef.current.getBoundingClientRect().left);
                }
                setShowCMInfoModal(true);
            }, 300);
            return () => clearTimeout(timer);
        } else {
            setShowCMInfoModal(false);
        }
    }, [isOpen, currentTerm.id]);

    useEffect(() => {
        setCurrentTab(tabId);
    }, [tabId]);

    useEffect(() => {
        activePeriod && dispatch(fetchPeriodExchangeRates({ periodId: activePeriod }));
    }, [activePeriod]);

    useEffect(() => {
        if (beneficiary.id !== currentTerm.beneficiarId) {
            dispatch(fetchBeneficiar(currentTerm.beneficiarId)).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    const isCurrencyDefault = resp.payload.statementCurrency === companyInfos?.currency;
                    setIsStatementCurrencyDefault(isCurrencyDefault);
                    setShowExchangeRate(usesMultipleCurrencies && !isCurrencyDefault && currentTab == 0);
                }
            });
        } else {
            const isCurrencyDefault = beneficiary.statementCurrency === companyInfos?.currency;
            setIsStatementCurrencyDefault(beneficiary.statementCurrency === companyInfos?.currency);
            setShowExchangeRate(usesMultipleCurrencies && !isCurrencyDefault && currentTab == 0);
        }
    }, [currentTerm]);

    const handleCloseStatementDrawer = (event: any) => {
        toggleDrawer("right", false)(event);
        dispatch(resetStatements());
    };

    return (
        currentTerm && (
            <>
                {hasCollectiveManagement &&
                    showCMInfoModal &&
                    createPortal(
                        <div
                            style={{
                                position: "fixed",
                                top: 0,
                                right: 0,
                                bottom: 0,
                                left: drawerLeft,
                                zIndex: 1300,
                                backgroundColor: "rgba(0,0,0,0.5)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <div
                                style={{
                                    backgroundColor: "white",
                                    borderRadius: "6px",
                                    fontFamily: "Poppins, sans-serif",
                                    width: "400px",
                                    maxWidth: "calc(100% - 32px)",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        padding: "16px 40px",
                                        borderBottom: "1px solid #e5e7eb",
                                    }}
                                >
                                    <h2
                                        style={{
                                            fontSize: "1rem",
                                            fontWeight: 500,
                                            margin: 0,
                                            fontFamily: "Poppins, sans-serif",
                                        }}
                                    >
                                        {t("Summary.CollectiveManagementDifferenceModalTitle")}
                                    </h2>
                                    <CloseIcon
                                        onClick={() => setShowCMInfoModal(false)}
                                        sx={{
                                            cursor: "pointer",
                                            color: "#B4B4B4",
                                            "&:hover": { color: "#626262" },
                                            transition: "0.4s",
                                        }}
                                    />
                                </div>
                                <div style={{ padding: "20px 40px 32px" }}>
                                    <p
                                        style={{
                                            fontSize: "0.875rem",
                                            color: "#6b7280",
                                            marginBottom: "32px",
                                            fontFamily: "Poppins, sans-serif",
                                            lineHeight: "1.5",
                                        }}
                                    >
                                        {t("Summary.CollectiveManagementDifferenceTooltip")}
                                    </p>
                                    <div style={{ display: "flex", justifyContent: "center" }}>
                                        <StyledButton buttonType="primary" onClick={() => setShowCMInfoModal(false)}>
                                            {t("Summary.GotIt")}
                                        </StyledButton>
                                    </div>
                                </div>
                            </div>
                        </div>,
                        document.body,
                    )}
                <Drawer
                    sx={{
                        "& .MuiDrawer-paper": {
                            overflow: "hidden",
                        },
                    }}
                    anchor={"right"}
                    open={isOpen}
                    onClose={handleCloseStatementDrawer}
                    PaperProps={{ ref: drawerPaperRef }}
                >
                    <div className={styles["summary-wrapper"]}>
                        <header className={styles["summary-header"]}>
                            <div className={styles["summary-header-main"]}>
                                <div className={styles["summary-header-title"]}>
                                    <TitleCover
                                        coverUrl={currentTerm.titleAvatarUrl}
                                        title={currentTerm.title}
                                        color="work-color"
                                    />
                                    <div className={styles["header-title-heading"]}>
                                        <h1>{currentTerm.title}</h1>
                                        <span className={styles["name-bold"]}>{currentTerm.author}</span>

                                        {/** Representative text */}
                                        {currentTerm?.representative && (
                                            <div className={styles["representative-option"]}>
                                                <span style={{ marginRight: "0.5rem" }}>
                                                    {t("TermRecap.Representee")}{" "}
                                                    <span>{currentTerm.representative || "-"}</span>
                                                </span>
                                                {currentTerm?.computeFromRepresentative && (
                                                    <Tooltip title={t("Representatives.Collapse.DiffProfileTooltip")}>
                                                        <div className={styles["associated-person-info"]}>
                                                            <ErrorOutlineIcon /> {t("Representatives.CalculationBase")}
                                                        </div>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className={styles["summary-header-close"]}>
                                    <ButtonBase
                                        variant="text"
                                        styles={{ border: "none" }}
                                        onClick={() => setOpen(false)}
                                    >
                                        <CloseIcon sx={{ color: "black" }} />
                                    </ButtonBase>
                                </div>
                            </div>
                            {showEchangeRate && (
                                <div className={styles["summary-exchange-rate"]}>
                                    <div>
                                        <span>{t("Summary.ExchangeRate")}</span>
                                    </div>
                                    <div className={styles["exchange-rate-details"]}>
                                        <div className={styles["rates"]}>
                                            <span>1 {companyInfos?.currency ?? "EUR"}</span>
                                            <img
                                                src={
                                                    companyInfos &&
                                                    getCurrencyData(companyInfos?.currency ?? "EUR")?.flag
                                                }
                                            />
                                        </div>
                                        <SwapHorizOutlinedIcon
                                            style={{
                                                color: variables["foreground"],
                                                height: "20px",
                                                width: "20px",
                                                opacity: "0.7",
                                            }}
                                        />
                                        <div className={styles["rates"]}>
                                            <span>
                                                {
                                                    periodExchangeRates.find(
                                                        (rate) =>
                                                            rate.currency == (beneficiary.statementCurrency ?? "EUR"),
                                                    )?.rate
                                                }{" "}
                                                {beneficiary.statementCurrency}
                                            </span>
                                            <img src={getCurrencyData(beneficiary.statementCurrency ?? "EUR")?.flag} />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </header>
                        <main>
                            <StyledTabs value={currentTab} onChange={handleChange}>
                                <Tab label={t("Summary.StatementOfRights")} value={0}></Tab>
                                <Tab label={t("Management.SalesTab")} value={1}></Tab>
                            </StyledTabs>
                            <TabPanel value={currentTab ?? 0} index={0}>
                                <TitleStatement
                                    term={currentTerm}
                                    companyCurrency={companyInfos?.currency}
                                    isStatementCurrencyDefault={isStatementCurrencyDefault}
                                    grossStatement={statement}
                                    activePeriod={activePeriod}
                                />
                            </TabPanel>
                            <TabPanel value={currentTab ?? 0} index={1}>
                                <SalesDetails
                                    termId={currentTerm.id}
                                    titleName={currentTerm.title}
                                    workId={currentTerm.workId}
                                    workAvatarUrl={currentTerm.titleAvatarUrl}
                                />
                            </TabPanel>
                        </main>
                    </div>
                </Drawer>
            </>
        )
    );
}

export default ManagementTitleDetails;
